"use client";
import Script from "next/script";
import { cn } from "@jetblue/kit/utilities/tailwind";
import { useIntersection } from "./use-intersection";
import { createRef, useCallback, useEffect, useState } from "react";

export type MMDataLayerItem = {
  destinationLocations: string | string[] | null;
  homeLocation: string | null;
  languageIsoCode: string;
  originLocations: string[] | null;
};

declare global {
  var MMdataLayer: MMDataLayerItem[];
}

type LivePricingProperties = {
  contentId: string;
  scriptUrl: string;
};

const once = (function_: () => void) => {
  let hasBeenCalled = false;
  return () => {
    if (hasBeenCalled) {
      return;
    }
    function_();
    hasBeenCalled = true;
  };
};

const onReady = once(() => {
  // eslint-disable-next-line unicorn/no-global-object-property-assignment -- must be global b/c third party
  globalThis.MMdataLayer = [
    {
      destinationLocations: null,
      homeLocation: null,
      languageIsoCode: "en",
      originLocations: ["NYC"],
    },
  ];
});

export const LivePricing = ({
  contentId,
  scriptUrl,
}: LivePricingProperties) => {
  const expectedIframeId = `mm${contentId}`;
  const reference = createRef<HTMLDivElement>();
  const { hasIntersected } = useIntersection({
    ref: reference,
    threshold: 0.1,
  });

  const [isLoading, setIsLoading] = useState(true);

  const onLoad = useCallback(
    (event: Event) => {
      const target = event.target as HTMLIFrameElement;
      if (target.id === expectedIframeId) {
        setIsLoading(false);
      }
    },
    [expectedIframeId],
  );

  const onError = useCallback(
    (event: Event) => {
      const target = event.target as HTMLIFrameElement;
      if (target.id === expectedIframeId) {
        setIsLoading(false);
      }
    },
    [expectedIframeId],
  );

  useEffect(() => {
    if (!hasIntersected || !reference.current) {
      return;
    }

    const container = reference.current;

    container.addEventListener("load", onLoad, { capture: true });
    container.addEventListener("error", onError, { capture: true });

    return () => {
      container.removeEventListener("load", onLoad, true);
      container.removeEventListener("error", onError, true);
    };
  }, [hasIntersected, contentId]);

  return (
    <div
      className="
        relative min-h-[699px]

        lg:min-h-[580px]
      "
      data-fs-element="pros"
      ref={reference}
    >
      {hasIntersected && (
        <Script
          id="em-container"
          onError={() => {
            setIsLoading(false);
          }}
          onReady={onReady}
          src={scriptUrl}
          strategy="afterInteractive"
        />
      )}
      {isLoading && (
        <div className="absolute inset-0">
          <div
            className="
              mx-auto h-10 w-[380px] rounded-xl
              bg-background-surface-container-variant-low
            "
          />
          <div
            className="
              mt-6 grid grid-cols-1 gap-6

              lg:grid-cols-3
            "
          >
            <SkeletonCard />
            <SkeletonCard
              className="
                hidden

                lg:block
              "
            />
            <SkeletonCard
              className="
                hidden

                lg:block
              "
            />
          </div>
        </div>
      )}
      <div data-container-id={contentId} />
    </div>
  );
};
type SkeletonCardProperties = {
  className?: string | undefined;
};

export const SkeletonCard = ({ className = "" }: SkeletonCardProperties) => (
  <div
    className={cn(
      `
        h-[22rem] animate-pulse space-y-4 rounded-2xl
        bg-background-surface-container p-4 shadow-lg
      `,
      className,
    )}
  >
    <div className="h-40 rounded-xl bg-background-surface-container-variant-low" />
    <div
      className="
        h-6 w-3/4 rounded-sm bg-background-surface-container-variant-low
      "
    />
    <div className="space-y-2">
      <div
        className="
          h-4 w-full rounded-sm bg-background-surface-container-variant-low
        "
      />
      <div
        className="
          h-4 w-5/6 rounded-sm bg-background-surface-container-variant-low
        "
      />
      <div
        className="
          h-4 w-2/3 rounded-sm bg-background-surface-container-variant-low
        "
      />
    </div>
  </div>
);
